import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Content from "../Layout/Content";
import { useAuth0 } from '@auth0/auth0-react';
import { FetchApi, PostApi, OverwriteApi, DeleteApi } from "../Api/AuthenticatedApi";
import { confirmIfLive } from "../utils/confirmIfLive";
import SummaryCard, { SummaryCardGroup } from "../Layout/SummaryCard";
import { Spin, Input, Button, Breadcrumb, Typography, Radio, Message, Switch } from "@arco-design/web-react";
import { IconEdit, IconArrowLeft } from "@arco-design/web-react/icon";
import { SuggestionsDialog as SuggestionDialog } from "../Broadcast/BroadcastEditor/MailVariants/SuggestionsDialog";
import FormBuilder from "./FormBuilder";
import { buildFormHtml, defaultFormContent, defaultFormDisplay, defaultFormFields, defaultFormStyle } from "./formHtml";
import { FormContentEditor, FormDisplayEditor, FormPreview, FormStyleEditor } from "./FormDesignEditor";
import DangerZone from "./Editors/DangerZone";
import { formDeleteNotes } from "./Editors/deleteNotes";
import RecordNotFound from "../Components/RecordNotFound";

const { Text } = Typography;
const ButtonGroup = Button.Group;


function SectionTitle({ children }) {
    return <div style={{ fontWeight: 600, margin: "16px 0 8px", color: "var(--color-text-1)" }}>{children}</div>;
}

export default function FormVariantEditor() {
    // campaignId/edit/form/:formVariantId
    const { campaignId, formVariantId } = useParams();
    const [record, setRecord] = React.useState({});
    const [loading, setLoading] = React.useState(true);
    const [loadFailed, setLoadFailed] = React.useState(false);
    const [campaignName, setCampaignName] = React.useState("");
    const [campaignForms, setCampaignForms] = React.useState([]); // for the delete confirmation
    const [deleting, setDeleting] = React.useState(false);

    // ui controls

    // controlled variables for the record
    const [shouldRun, setShouldRun] = React.useState(false)
    const [variantName, setVariantName] = React.useState("");
    const [subject, setSubject] = React.useState("");
    const [htmlContent, setHtmlContent] = React.useState("");
    const [textContent, setTextContent] = React.useState("");

    // Form builder schema
    const [layout, setLayout] = React.useState("builder"); // "builder" | "custom_html"
    const [contactTypeId, setContactTypeId] = React.useState("");
    const [formFields, setFormFields] = React.useState(defaultFormFields());
    const [formContent, setFormContent] = React.useState(defaultFormContent());
    const [formStyle, setFormStyle] = React.useState(defaultFormStyle());
    const [formDisplay, setFormDisplay] = React.useState(defaultFormDisplay());
    const [previewThanks, setPreviewThanks] = React.useState(false);

    // controlled variables for non-record
    const [suggestionTopic, setSuggestionTopic] = React.useState("");

    const { getAccessTokenSilently } = useAuth0();
    const history = useNavigate();

    const save = async () => {
        const ok = await confirmIfLive({ live: isLive, title: "This form is live", content: liveReason });
        if (!ok) return;
        // In builder mode the served HTML is generated from the schema; custom-HTML
        // mode persists the raw textarea as-is.
        const html = layout === "builder" ? buildFormHtml(formFields, contactTypeId, formContent, formStyle) : htmlContent;
        saveVariant({
            name: variantName,
            should_run: shouldRun,
            form_html_template: html,
            mail_text_template: textContent,
            mail_subject_template: subject,
            form_schema: {
                layout,
                contact: { type_id: contactTypeId || null, fields: formFields },
                content: formContent,
                style: formStyle,
                display: formDisplay,
                collect_company: false,
            },
        });
    }

    const safeSetString = (setter, item) => {
        if (!!item) {
            setter(item)
        } else {
            setter("")
        }
    }

    const setFromRecord = (r) => {
        safeSetString(setVariantName, r.name)
        safeSetString(setSubject, r.mail_subject_template)
        safeSetString(setHtmlContent, r.form_html_template)
        safeSetString(setTextContent, r.mail_text_template)
        safeSetString(setSuggestionTopic, r.topic)  // temporary copy from stored topic to the suggestion variable

        setShouldRun(r.should_run)

        // Form builder schema (falls back to the custom-HTML mode for legacy forms).
        const fs = r.form_schema || {};
        const contact = fs.contact || {};
        setLayout(fs.layout || (r.form_html_template ? "custom_html" : "builder"));
        setContactTypeId(contact.type_id || "");
        setFormFields((contact.fields && contact.fields.length) ? contact.fields : defaultFormFields());
        setFormContent({ ...defaultFormContent(), ...(fs.content || {}) });
        setFormStyle({ ...defaultFormStyle(), ...(fs.style || {}) });
        setFormDisplay({ ...defaultFormDisplay(), ...(fs.display || {}) });
    }

    useEffect(() => {
        if (!formVariantId) {
            setLoading(false); // new form — nothing to load (route is /form/new)
            return;
        }
        (async () => {
            let url = "/api/campaigns/" + campaignId + "/form/variants/" + formVariantId
            FetchApi(getAccessTokenSilently, url,
                (data) => {
                    setLoading(false);
                    setRecord(data);
                    setFromRecord(data);
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    setLoadFailed(true);
                })
        })();
    }, [campaignId, setRecord, setLoading, history, getAccessTokenSilently, formVariantId])

    // The form-variant record doesn't carry the campaign name; fetch the campaign
    // just for the breadcrumb so the user can see where they are and get back.
    useEffect(() => {
        if (!campaignId) return;
        FetchApi(getAccessTokenSilently, "/api/campaigns/" + campaignId,
            (data) => {
                setCampaignName((data && data.name) || "");
                setCampaignForms((data && data.form_variants) || []);
            },
            () => {}, () => {});
    }, [campaignId, getAccessTokenSilently])

    // Delete (archive) this form, then back to the campaign's forms.
    const deleteForm = () => new Promise((resolve, reject) => {
        setDeleting(true);
        DeleteApi(getAccessTokenSilently, `/api/campaigns/${campaignId}/form/variants/${formVariantId}`,
            () => {
                setDeleting(false);
                Message.success("Form deleted");
                history(`/signups/${campaignId}/edit?section=forms`);
                resolve();
            },
            () => { setDeleting(false); reject(); },
            (err) => { setDeleting(false); Message.error((err && err.message) || "Couldn't delete the form"); reject(err); });
    });

    if (loadFailed) {
        return <RecordNotFound what="form" backTo={`/signups/${campaignId}/edit`} backLabel="Back to the signup" />;
    }
    if (loading) {
        return (
            <Content>
                <Spin />
            </Content>
        );
    }

    function saveVariant(data) {
        if (!formVariantId) {
            createVariant(data);
            return;
        }
        (async () => {
            // Backend registers this path as PUT (HandleModifyFormVariant).
            let url = "/api/campaigns/" + campaignId + "/form/variants/" + formVariantId;
            OverwriteApi(getAccessTokenSilently, url,
                (saved) => {
                    setLoading(false);
                    setRecord(saved);
                    Message.success("Form saved");
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    Message.error("Failed to save form");
                    console.error("Failed to save form variant", response);
                }, data);
        })();
    }

    // For a brand-new form: POST to create, then route to the real id.
    function createVariant(data) {
        (async () => {
            let url = "/api/campaigns/" + campaignId + "/form/variants/";
            PostApi(getAccessTokenSilently, url,
                (created) => {
                    Message.success("Form created");
                    history("/signups/" + campaignId + "/edit/form/" + created.id);
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    Message.error("Failed to create form");
                    console.error("Failed to create form variant", response);
                }, data);
        })();
    }

    const linkStyle = { cursor: "pointer", color: "rgb(var(--arcoblue-6))" };

    const views = (record && record.views) || 0;
    const isLive = !!(record && record.should_run) && views > 0;
    const liveReason = `This form is active and has received ${views.toLocaleString()} view${views === 1 ? "" : "s"}. Saving changes the form people are filling out.`;

    return (
        <Content>
            <div>
                <Breadcrumb separator="›">
                    <Breadcrumb.Item style={linkStyle} onClick={() => history("/")}>Home</Breadcrumb.Item>
                    <Breadcrumb.Item style={linkStyle} onClick={() => history("/signups")}>Signups</Breadcrumb.Item>
                    <Breadcrumb.Item style={linkStyle} onClick={() => history(`/signups/${campaignId}/edit?section=forms`)}>
                        {campaignName || "Campaign"}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item><Text>Edit Form</Text></Breadcrumb.Item>
                </Breadcrumb>
            </div>

            <div style={{ margin: "12px 0" }}>
                <Button
                    icon={<IconArrowLeft />}
                    onClick={() => history(`/signups/${campaignId}/edit?section=forms`)}
                >
                    Back to {campaignName || "campaign"}
                </Button>
            </div>

            <div>
                <SummaryCardGroup>
                    <SummaryCard title={"Views"} value={record.views} />
                    <SummaryCard title={"Conversions"} value={record.conversions} />
                </SummaryCardGroup>

                <SummaryCard title={"Form Content"}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 20, marginBottom: 16 }}>
                        <div style={{ flex: "1 1 260px", minWidth: 220 }}>
                            <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>Form name</div>
                            <Input
                                value={variantName}
                                placeholder="e.g. Homepage signup"
                                onChange={setVariantName}
                            />
                        </div>
                        <div>
                            <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>Status</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, height: 32 }}>
                                <Switch checked={shouldRun} onChange={setShouldRun} />
                                <span style={{ color: "var(--color-text-2)" }}>{shouldRun ? "Active" : "Paused"}</span>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginBottom: 12 }}>
                        <Radio.Group
                            type="button"
                            value={layout}
                            onChange={setLayout}
                            options={[{ label: "Field builder", value: "builder" }, { label: "Custom HTML", value: "custom_html" }]}
                        />
                    </div>

                    {layout === "builder" ? (
                        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 24 }}>
                            <div style={{ flex: "1 1 380px", minWidth: 300 }}>
                                <SectionTitle>Display</SectionTitle>
                                <FormDisplayEditor display={formDisplay} setDisplay={setFormDisplay} />
                                <SectionTitle>Content</SectionTitle>
                                <FormContentEditor content={formContent} setContent={setFormContent} />
                                <SectionTitle>Style</SectionTitle>
                                <FormStyleEditor style={formStyle} setStyle={setFormStyle} />
                                <SectionTitle>Fields</SectionTitle>
                                <FormBuilder
                                    typeId={contactTypeId}
                                    setTypeId={setContactTypeId}
                                    fields={formFields}
                                    setFields={setFormFields}
                                />
                            </div>
                            <div style={{ flex: "1 1 420px", minWidth: 300, position: "sticky", top: 16 }}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <SectionTitle>Preview</SectionTitle>
                                    <Radio.Group
                                        type="button"
                                        size="small"
                                        value={previewThanks ? "thanks" : "form"}
                                        onChange={(v) => setPreviewThanks(v === "thanks")}
                                        options={[{ label: "Form", value: "form" }, { label: "After signup", value: "thanks" }]}
                                    />
                                </div>
                                <FormPreview
                                    html={buildFormHtml(formFields, contactTypeId, formContent, formStyle, { showThanks: previewThanks })}
                                    popup={formDisplay.mode === "popup"}
                                />
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Custom-HTML escape hatch: hand-coded HTML form. */}
                            <Input.TextArea
                                placeholder="Form HTML"
                                autoSize={{ minRows: 16 }}
                                value={htmlContent}
                                onChange={(v) => setHtmlContent(v)}
                                style={{ fontFamily: "monospace", fontSize: 13 }}
                            />
                            <SuggestionDialog
                                data={{ a: 'foo' }}
                                render={(open) => (
                                    <ButtonGroup>
                                        <Button type="text" icon={<IconEdit />} onClick={open}>
                                            Suggestions For Webform
                                        </Button>
                                        <Button type="text" icon={<IconEdit />} onClick={open}>
                                            Templates for Webform
                                        </Button>
                                    </ButtonGroup>
                                )}
                                dataSetter={setHtmlContent}
                                marketingType={"body_html"}
                            />
                        </>
                    )}
                    <div>&nbsp;</div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Button type="primary" onClick={save}>Save</Button>
                        <Button onClick={() => setFromRecord(record)}>Discard</Button>
                    </div>
                </SummaryCard>

                {formVariantId && (
                    <DangerZone
                        description="Delete this form from the campaign. It stops being shown on your site."
                        buttonLabel="Delete form"
                        confirmTitle={`Delete “${variantName || "this form"}”?`}
                        notes={formDeleteNotes(formVariantId, campaignForms)}
                        onConfirm={deleteForm}
                        loading={deleting}
                    />
                )}
            </div>
        </Content>
    );
}
